// OurProcess.jsx


import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../assets/styles/Process.css';
const { processSteps } = require('../../data/ProcessData.js');

export default function OurProcess() {
  const [activeStep, setActiveStep] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const autoplayRef = useRef(null);
  const containerRef = useRef(null);

  // Configuration: autoplay interval in ms
  const AUTO_PLAY_INTERVAL = 5000; // 5s per step

  const current = processSteps.find((s) => s.step === activeStep);
  const stepsCount = processSteps.length;

  // Respect user's reduced-motion preference
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const goToStep = useCallback((n) => {
    // wrap around between 1..stepsCount
    const next = ((n - 1 + stepsCount) % stepsCount) + 1;
    setActiveStep(next);
  }, [stepsCount]);

  useEffect(() => {
    // clear prior
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }

    if (isPlaying && !prefersReducedMotion) {
      autoplayRef.current = setInterval(() => {
        setActiveStep((s) => ((s % stepsCount) + 1));
      }, AUTO_PLAY_INTERVAL);
    }

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, [isPlaying, stepsCount, prefersReducedMotion]);

  // Pause when user hovers or focuses the stepper (better UX)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onEnter = () => setIsPlaying(false);
    const onLeave = () => setIsPlaying(true);

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('focusin', onEnter);
    el.addEventListener('focusout', onLeave);

    return () => {
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('focusin', onEnter);
      el.removeEventListener('focusout', onLeave);
    };
  }, []);

  // Keyboard navigation: left/right arrows to navigate, space to toggle play/pause
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToStep(activeStep + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToStep(activeStep - 1);
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        // toggle play/pause
        setIsPlaying((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeStep, goToStep]);

  return (
    <section
      className="process-stepper-wrapper min-h-screen py-16 px-4 md:px-10 lg:px-24 bg-gradient-to-b from-sky-50 via-cyan-50 to-sky-100"
    >
      <div className="max-w-4xl mx-auto">
        <h2 className="text-center text-4xl md:text-5xl font-extrabold mb-4 text-sky-950 tracking-tight drop-shadow-sm">
          Our Process
        </h2>
        <p className="text-center text-lg md:text-xl text-sky-900 mb-12 font-medium">
          Delivering end-to-end digital services—from design and development to deployment—<br className="hidden md:block" />
          transforming your vision into measurable success.
        </p>

        {/* Stepper */}
        <div className="flex flex-col items-center" ref={containerRef}>
          <div className="w-full flex justify-between items-center relative mb-12">
            {/* Step lines */}
            <div className="absolute top-1/2 left-0 w-full h-1 bg-sky-100 z-0" style={{transform: 'translateY(-50%)'}} />
            {processSteps.map((step, idx) => (
              <button
                key={step.step}
                className={`relative z-10 flex flex-col items-center focus:outline-none group`}
                onClick={() => { setActiveStep(step.step); setIsPlaying(false); }}
                aria-label={`Go to step ${step.step}`}
                aria-pressed={activeStep === step.step}
              >
                <div
                  className={`flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full border-4 transition-all duration-300 text-xl md:text-2xl font-bold shadow-lg
                    ${activeStep === step.step
                      ? 'bg-sky-500 border-sky-400 text-white scale-110 ring-4 ring-sky-200'
                      : 'bg-white border-sky-200 text-sky-500 hover:bg-sky-100'}
                  `}
                  style={{
                    background: activeStep === step.step ? (step.color || '#0ea5e9') : undefined,
                  }}
                >
                  {step.step}
                </div>
                <span className={`mt-2 text-xs md:text-sm font-medium transition-colors duration-200 ${activeStep === step.step ? 'text-sky-700' : 'text-sky-400 group-hover:text-sky-600'}`}>{step.title}</span>
              </button>
            ))}
          </div>

          {/* Step details */}
          <div className="w-full max-w-2xl mx-auto bg-white/80 rounded-3xl shadow-xl border border-sky-100 p-6 md:p-8 flex flex-col items-center transition-all duration-500 min-h-[220px]">
            <div className="w-full flex items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md"
                  style={{background: current.color || '#0ea5e9'}}>
                  {current.step}
                </div>
                <h3 className="text-2xl font-semibold text-sky-900">{current.title}</h3>
              </div>

              {/* Play / Pause control */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsPlaying((p) => !p)}
                  className="px-3 py-1 rounded-full bg-sky-100 text-sky-700 hover:bg-sky-200 focus:outline-none shadow-sm"
                  aria-label={isPlaying ? 'Pause autoplay' : 'Start autoplay'}
                >
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
              </div>
            </div>

            <p className="text-gray-700 text-base md:text-lg text-center" aria-live="polite">{current.description}</p>
          </div>
        </div>
      </div>
    </section>
  );
}